document.addEventListener("DOMContentLoaded", () => {
  // ============================================
  // Initialization Guard
  // ============================================
  if (window.psykhe?._initialized) {
    console.warn(
      "[Psykhe] Already initialized, skipping duplicate initialization",
    );
    return;
  }

  // ============================================
  // Configuration
  // ============================================
  const config = window.psykheConfig || {};
  const DEBOUNCE_DELAY = config.debounceDelay || 250;
  const SEARCH_TRACKING_DELAY = config.searchTrackingDelay || 750;
  const API_TIMEOUT = config.apiTimeout || 20000;
  const MIN_QUERY_LENGTH = config.minQueryLength || 1;
  const MAX_PRODUCTS_TO_SHOW = config.maxProducts || 6;
  const MAX_FACETS_TO_SHOW = config.maxFacets || 8;
  const MIN_CAROUSEL_ITEMS = config.minCarouselItems || 2;
  const FALLBACK_COOLDOWN_MS = config.fallbackCooldownMs || 60000;
  const API_URL = config.apiUrl || "https://api.psykhe.dev/v1/recommend/search";
  const CAROUSELS_API_URL =
    config.carouselsApiUrl || "https://api.psykhe.dev/v1/recommend/carousel";
  const CLIENT_ID = config.clientId || "kirnazabete";
  const BASE_SEARCH_URL = "/collections/psearch";

  // ============================================
  // Constants
  // ============================================
  const FACET_KEY_CATEGORY = "p.f.pr.pcategory";
  const FACET_KEY_VENDOR = "p.f.pr.vendor";
  const TRACKING_EVENT_SEARCH = "psykhe_search";
  const TRACKING_EVENT_PROMO_CLICK = "psykhe_promo_click";
  const TRACKING_EVENT_LIST_VIEW = "psykhe_list_view";
  const TRACKING_EVENT_LIST_CLICK = "psykhe_list_click";
  const USER_ERROR_MESSAGE = "Something went wrong. Please try again.";

  const MAX_API_FAILURES = config.maxApiFailures || 3;
  const SHOPIFY_SEARCH_URL = window.Shopify?.routes?.root
    ? `${window.Shopify.routes.root}search/suggest.json`
    : "/search/suggest.json";
  const FALLBACK_SEARCH_FIELDS = "title,product_type,variants.title,vendor";

  const RECOMMENDATION_IMAGE_SIZES =
    "(min-width: 1200px) 268px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 90px) / 3), calc((100vw - 32px) / 2)";

  const RECOMMENDATION_IMAGE_WIDTHS = [240, 320, 400, 480, 560, 600];

  // ============================================
  // Debug Logging
  // ============================================
  const debugLog = (message, data) => {
    if (config.debug) console.log("[Psykhe]", message, data ?? "");
  };

  const debugWarn = (message, error) => {
    if (config.debug) console.warn("[Psykhe]", message, error);
  };

  // ============================================
  // Utility Functions
  // ============================================
  const getCurrency = () => window.Shopify?.currency?.active || "USD";

  const toTitleCase = (str) =>
    str
      ? str
          .split(" ")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ")
      : "";

  const formatPrice = (price) => {
    if (price == null || isNaN(price)) return "";
    try {
      if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
        return Shopify.formatMoney(price * 100, theme.moneyFormat).replace(
          ".00",
          "",
        );
      }
      return new Intl.NumberFormat(
        window.Shopify?.locale || navigator.language || "en-US",
        {
          style: "currency",
          currency: getCurrency(),
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        },
      ).format(price);
    } catch {
      return `$${Math.round(price).toLocaleString()}`;
    }
  };

  const getImageSrc = (raw) => {
    if (!raw) return "";
    return typeof raw === "string" ? raw : raw?.src || "";
  };

  const resizeShopifyImage = (url, width = 500) => {
    if (!url || typeof url !== "string") return url;

    if (url.includes("cdn.shopify.com")) {
      const cleaned = url
        .replace(/([?&]width=)\d+/g, "")
        .replace(/(_\d+x\d*)(?=\.[a-zA-Z]+(?:\?|$))/, "");

      const separator = cleaned.includes("?") ? "&" : "?";
      return `${cleaned}${separator}width=${width}`;
    }

    return url.replace(/(_\d+x\d*)?(\.[^.?]+)(\?.*)?$/, `_${width}x$2$3`);
  };

  const buildShopifySrcset = (url, widths = RECOMMENDATION_IMAGE_WIDTHS) => {
    const base = getImageSrc(url);
    if (!base) return "";

    return widths.map((width) => `${resizeShopifyImage(base, width)} ${width}w`).join(", ");
  };

  const debounce = (func, delay) => {
    let timer;
    const debouncedFn = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
    debouncedFn.cancel = () => {
      clearTimeout(timer);
      timer = null;
    };
    return debouncedFn;
  };

  const createTimeoutSignal = (timeoutMs) => {
    if (
      typeof AbortSignal !== "undefined" &&
      typeof AbortSignal.timeout === "function"
    ) {
      return AbortSignal.timeout(timeoutMs);
    }
    return null;
  };

  const mergeSignals = (signal, timeoutSignal) => {
    if (signal && timeoutSignal) {
      if (
        typeof AbortSignal !== "undefined" &&
        typeof AbortSignal.any === "function"
      ) {
        return AbortSignal.any([signal, timeoutSignal]);
      }
      return combineAbortSignals(signal, timeoutSignal);
    }
    return signal || timeoutSignal || undefined;
  };

  const buildSearchUrl = (query, facetKey = null, facetValue = null) => {
    try {
      const url = new URL(BASE_SEARCH_URL, window.location.origin);
      url.searchParams.set("q", query);
      if (facetKey && facetValue) url.searchParams.set(facetKey, facetValue);
      return url.toString();
    } catch {
      const base = `${BASE_SEARCH_URL}?q=${encodeURIComponent(query)}`;
      return facetKey && facetValue
        ? `${base}&${encodeURIComponent(facetKey)}=${encodeURIComponent(facetValue)}`
        : base;
    }
  };

  let srAnnouncer = null;
  let srAnnouncementTimer = null;
  const announceToScreenReader = (message, priority = "polite") => {
    if (!srAnnouncer) {
      srAnnouncer = document.createElement("div");
      srAnnouncer.setAttribute("aria-live", priority);
      srAnnouncer.setAttribute("aria-atomic", "true");
      srAnnouncer.className = "sr-only";
      srAnnouncer.style.cssText =
        "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;";
      document.body.appendChild(srAnnouncer);
    }
    clearTimeout(srAnnouncementTimer);
    srAnnouncer.setAttribute("aria-live", priority);
    srAnnouncer.textContent = "";
    srAnnouncementTimer = setTimeout(() => {
      srAnnouncer.textContent = message;
    }, 100);
  };

  const cancelSrAnnouncement = () => {
    clearTimeout(srAnnouncementTimer);
    srAnnouncementTimer = null;
    if (srAnnouncer) srAnnouncer.textContent = "";
  };

  const cleanupSrAnnouncer = () => {
    cancelSrAnnouncement();
    if (srAnnouncer) {
      srAnnouncer.remove();
      srAnnouncer = null;
    }
  };

  // ============================================
  // Psykhe API Setup
  // ============================================
  window.psykhe = window.psykhe || {};
  window.psykhe.config = {
    apiUrl: API_URL,
    clientId: CLIENT_ID,
    facets: [FACET_KEY_CATEGORY, FACET_KEY_VENDOR],
    projection: [
      "identifier",
      "handle",
      "name",
      "vendor",
      "images",
      "tags",
      "variants[].identifier",
      "variants[].attributes.price",
      "variants[].attributes.compareAtPrice",
    ],
  };

  window.psykhe.getSessionId = () => {
    try {
      const token = window.ShopifyAnalytics?.lib
        ?.user?.()
        ?.traits?.()?.uniqToken;
      if (token) return String(token);
    } catch {
      /* ignore */
    }
    try {
      return crypto.randomUUID();
    } catch {
      /* ignore */
    }
    return window.Shopify?.designMode ? "design-mode" : "";
  };

  window.psykhe.getUserId = () => {
    const id = window.ShopifyAnalytics?.meta?.page?.customerId;
    return id ? String(id) : null;
  };

  window.psykhe.search = (query, options = {}) => {
    const requestData = {
      client_id: window.psykhe.config.clientId,
      query,
      session_id: window.psykhe.getSessionId(),
      user_id: options.userId ?? window.psykhe.getUserId(),
      facets: window.psykhe.config.facets,
      metadata: {},
      projection: window.psykhe.config.projection,
      offset: options.offset || 0,
      limit: options.limit || 6,
      compute_redirect: true,
      filter: {
        must: [{ key: "attributes.isSellable", match: { value: true } }],
        must_not: [
          {
            key: "attributes.tags",
            match: { any: ["gatedonline", "testgatedonline"] },
          },
        ],
      },
    };

    const timeoutSignal = createTimeoutSignal(API_TIMEOUT);
    const signal = mergeSignals(options.signal, timeoutSignal);

    return fetch(window.psykhe.config.apiUrl, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: JSON.stringify(requestData),
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error("HTTP error " + response.status);
      return response.json();
    });
  };

  const combineAbortSignals = (signal1, signal2) => {
    if (typeof AbortController === "undefined" || !signal1 || !signal2) {
      return signal1 || signal2 || undefined;
    }
    const controller = new AbortController();

    const onAbort = () => {
      controller.abort();
    };

    signal1.addEventListener("abort", onAbort, { once: true });
    signal2.addEventListener("abort", onAbort, { once: true });

    if (signal1.aborted || signal2.aborted) {
      controller.abort();
    }

    return controller.signal;
  };

  // ============================================
  // Recommendation Card Rendering
  // ============================================
  const getAltImage = (product) => {
    const images = product.images;
    if (!Array.isArray(images) || images.length <= 1) return "";
    for (let i = 1; i < images.length; i++) {
      const src =
        typeof images[i] === "string" ? images[i] : images[i]?.src || "";
      if (src && !src.includes("_swatch") && !src.includes("_detail")) {
        return src;
      }
    }
    return "";
  };

  const FIELD_MAP = {
    url: (product) => `/products/${encodeURIComponent(product.handle)}`,
    image: (product) => getImageSrc(product.main_image || product.images?.[0]),
    altImage: getAltImage,
    name: (product) => product.name || "",
    vendor: (product) => product.vendor || "",
    price: (product) => {
      const v = product.variants?.[0];
      return v?.attributes?.price != null
        ? formatPrice(v.attributes.price)
        : "";
    },
    compareAtPrice: (product) => {
      const v = product.variants?.[0];
      const compare = v?.attributes?.compareAtPrice;
      const current = v?.attributes?.price;
      return compare != null && compare > current ? formatPrice(compare) : "";
    },
    savePercent: (product) => {
      const v = product.variants?.[0];
      const compare = v?.attributes?.compareAtPrice;
      const current = v?.attributes?.price;
      if (compare != null && current != null && compare > current) {
        return `Save ${Math.round(((compare - current) / compare) * 100)}%`;
      }
      return "";
    },
    swymButton: (product) => product.identifier || "",
  };

  const optimizeRenderedRecommendationImages = (container) => {
    if (!container) return;

    const cards = container.querySelectorAll(".item, .psykhe-product-card");

    cards.forEach((card, cardIndex) => {
      const images = card.querySelectorAll("img");
      if (!images.length) return;

      images.forEach((img, imgIndex) => {
        const isPrimaryImage = imgIndex === 0;
        const isPriority = isPrimaryImage && cardIndex < 2;

        img.loading = isPriority ? "eager" : "lazy";
        img.fetchPriority = isPriority ? "high" : "auto";
        img.decoding = "async";
        img.sizes = RECOMMENDATION_IMAGE_SIZES;

        const rawSrc =
          img.getAttribute("src") ||
          img.currentSrc ||
          "";
        const rawSrcset = img.getAttribute("srcset") || "";

        const sourceUrl =
          rawSrc ||
          rawSrcset.split(",")[0]?.trim().split(" ")[0] ||
          "";

        if (!sourceUrl || !sourceUrl.includes("cdn.shopify.com")) return;

        img.src = resizeShopifyImage(sourceUrl, 600);
        img.srcset = buildShopifySrcset(sourceUrl);

        if (!img.getAttribute("width")) img.width = 600;
        if (!img.getAttribute("height")) img.height = 750;
      });
    });
  };

  window.psykhe.renderCard = (template, product) => {
    const clone = template.content.cloneNode(true);
    const hasAltImage = !!getAltImage(product);

    clone.querySelectorAll("[data-psykhe]").forEach((el) => {
      const field = el.getAttribute("data-psykhe");
      const mapper = FIELD_MAP[field];
      if (!mapper) return;
      const value = mapper(product);

      if (el.tagName === "IMG") {
        if (!value) {
          const wrapper = el.closest(".image");
          if (wrapper) wrapper.remove();
          else el.remove();
          return;
        }

        const isFeatured = field === "image";
        const base = getImageSrc(value);

        el.src = resizeShopifyImage(base, 600);
        el.srcset = buildShopifySrcset(base);
        el.sizes = RECOMMENDATION_IMAGE_SIZES;
        el.alt = product.name || "Product image";
        el.width = 600;
        el.height = 750;
        el.loading = "lazy";
        el.fetchPriority = "auto";
        el.decoding = "async";

        if (isFeatured && !hasAltImage) {
          el.classList.add("no-alt-image");
        }
      } else if (el.tagName === "BUTTON" && field === "swymButton") {
        if (!value) {
          el.remove();
          return;
        }
        const variantId = product.variants?.[0]?.identifier || "";
        el.classList.add(`product_${value}`);
        el.setAttribute("data-product-id", value);
        if (variantId) el.setAttribute("data-variant-id", variantId);
        el.setAttribute(
          "data-product-url",
          `${window.location.origin}/products/${product.handle}`,
        );
      } else if (el.tagName === "A") {
        if (value) el.href = value;
      } else {
        if (!value) {
          el.remove();
          return;
        }
        el.textContent = value;
      }
    });

    return clone;
  };

  const RECO_API_TYPES = {
    similar: "more_like_this",
    brand: "you_might_also_like",
    404: "you_might_also_like",
    cart: "more_like_this",
    "cart-empty": "you_might_also_like",
  };

  window.psykhe.carousels = (payload = {}) => {
    const requestData = {
      client_id: window.psykhe.config.clientId,
      session_id: window.psykhe.getSessionId(),
      user_id: window.psykhe.getUserId(),
      ...payload,
    };

    const timeoutSignal = createTimeoutSignal(API_TIMEOUT);

    return fetch(CAROUSELS_API_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: JSON.stringify(requestData),
      signal: timeoutSignal || undefined,
    }).then((response) => {
      if (!response.ok) throw new Error("HTTP error " + response.status);
      return response.json();
    });
  };

  const buildCarouselPayload = (container, extraExcludeIds = []) => {
    const recoType = container.getAttribute("data-psykhe-reco");
    const seed = container.getAttribute("data-psykhe-seed");
    const vendor = container.getAttribute("data-psykhe-vendor");
    const designer = container.getAttribute("data-psykhe-designer");
    const collection = container.getAttribute("data-psykhe-collection");
    const excludeIds = container.getAttribute("data-psykhe-exclude");
    const apiType = RECO_API_TYPES[recoType] || "you_might_also_like";

    const mustFilters = [
      { key: "attributes.isSellable", match: { value: true } },
    ];
    if (collection) {
      mustFilters.push({
        key: "attributes.collectionHandles",
        match: { any: [collection] },
      });
    } else {
      mustFilters.push({
        key: "attributes.collectionHandles",
        match: { any: ["psearch"] },
      });
    }

    const shouldFilters = [];
    if (vendor) {
      shouldFilters.push({
        key: "attributes.vendor",
        match: { any: [vendor] },
      });
    }
    if (designer) {
      shouldFilters.push({
        key: "attributes.tags",
        match: { any: [designer] },
      });
    }

    const attrExcludeIds = excludeIds
      ? excludeIds
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const mustNotFilters = [];
    const ids = [...new Set([...attrExcludeIds, ...extraExcludeIds])];
    if (ids.length) {
      mustNotFilters.push({
        key: "identifier",
        match: { any: ids },
      });
    }

    const filter = { must: mustFilters };
    if (mustNotFilters.length) {
      filter.must_not = mustNotFilters;
    }
    if (shouldFilters.length) {
      filter.should = shouldFilters;
    }

    const payload = {
      type: apiType,
      count: 4,
      projection: window.psykhe.config.projection,
      filter,
      metadata: {
        carousel_size: "4",
        placement: recoType,
      },
    };

    if (seed && apiType === "more_like_this") {
      payload.seedIdentifiers = seed.split(",").map((s) => s.trim());
    }

    return payload;
  };

  window.psykhe.initRecommendations = () => {
    const template = document.getElementById("psykhe-product-card");
    if (!template) {
      debugLog(
        "No psykhe-product-card template found, skipping recommendations",
      );
      return;
    }

    const containers = document.querySelectorAll("[data-psykhe-reco]");
    if (!containers.length) {
      debugLog("No recommendation containers found");
      return;
    }

    const setCarouselTabRendered = (container, rendered) => {
      const productBottom = container.closest(".product-bottom");
      const tab = container.closest("[data-tab]");
      if (!productBottom || !tab) return;

      const tabName = tab.getAttribute("data-tab");
      const tabButton = productBottom.querySelector(
        `button[data-tab="${tabName}"]`,
      );

      if (rendered) {
        productBottom.classList.remove("hide");
        tab.dataset.psykheRendered = "true";
        tab.classList.remove("hide");
        if (tabButton) tabButton.classList.remove("hide");
        return;
      }

      delete tab.dataset.psykheRendered;
      tab.classList.add("hide");
      if (tabButton) tabButton.classList.add("hide");
    };

    const markCarouselTabRendered = (container) => {
      const tab = container.closest("[data-tab]");
      if (tab) tab.dataset.psykheRendered = "true";
    };

    const isCarouselTabRendered = (container) => {
      const tab = container.closest("[data-tab]");
      return Boolean(tab && tab.dataset.psykheRendered === "true");
    };

    const processCarousel = (container, extraExcludeIds = [], options = {}) => {
      const recoType = container.getAttribute("data-psykhe-reco");
      const payload = buildCarouselPayload(container, extraExcludeIds);
      const enforceMinimumItems = Boolean(container.closest(".product-bottom"));
      const revealAfterRender = options.revealAfterRender !== false;

      debugLog("Fetching recommendations", { recoType, payload });

      return window.psykhe
        .carousels(payload)
        .then((response) => {
          const data = response.data || response;
          const items = data.items || [];
          if (!items.length) {
            setCarouselTabRendered(container, false);
            return [];
          }

          const uniqueProducts = [];
          const seenProductKeys = new Set();
          items.forEach((product) => {
            if (!product.handle) return;
            const productKey = product.identifier
              ? `id:${String(product.identifier)}`
              : `handle:${product.handle}`;
            if (seenProductKeys.has(productKey)) return;
            seenProductKeys.add(productKey);
            uniqueProducts.push(product);
          });

          const fragment = document.createDocumentFragment();
          const renderedIds = [];
          const renderedProducts = [];
          uniqueProducts.forEach((product) => {
            fragment.appendChild(window.psykhe.renderCard(template, product));
            renderedProducts.push(product);
            if (product.identifier) {
              renderedIds.push(String(product.identifier));
            }
          });
          if (!renderedProducts.length) {
            setCarouselTabRendered(container, false);
            return [];
          }
          if (
            enforceMinimumItems &&
            renderedProducts.length < MIN_CAROUSEL_ITEMS
          ) {
            debugLog(
              `Too few renderable recommendation items (${renderedProducts.length}) for ${recoType}, keeping carousel hidden`,
            );
            setCarouselTabRendered(container, false);
            return [];
          }
          container.appendChild(fragment);
          optimizeRenderedRecommendationImages(container);

          const recoId = data.reco_id || null;
          const productList = `carousel:${recoType}`;
          const trackingProducts = buildTrackingProducts(renderedProducts);

          if (
            recoId &&
            trackingProducts.length &&
            "IntersectionObserver" in window
          ) {
            const viewObserver = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    trackCarouselView({
                      productList,
                      recommendationId: recoId,
                      products: trackingProducts,
                    });
                    viewObserver.disconnect();
                  }
                });
              },
              { threshold: 0.3 },
            );
            viewObserver.observe(container);
          }

          container
            .querySelectorAll("[data-product-id]")
            .forEach((card, idx) => {
              const product = trackingProducts[idx];
              if (!product || !recoId) return;

              card.addEventListener("click", () => {
                trackCarouselClick({
                  productList,
                  recommendationId: recoId,
                  product,
                });
              });
            });

          container.dispatchEvent(
            new CustomEvent("psykhe:recommendations-rendered", {
              bubbles: true,
              detail: { type: recoType, count: renderedProducts.length },
            }),
          );

          // reveal only on a successful render so a sparse carousel stays hidden.
          if (revealAfterRender) {
            setCarouselTabRendered(container, true);
          } else {
            markCarouselTabRendered(container);
          }

          debugLog("Rendered recommendations", {
            recoType,
            count: renderedProducts.length,
          });
          return renderedIds;
        })
        .catch((error) => {
          debugWarn("Recommendation fetch failed for " + recoType, error);
          setCarouselTabRendered(container, false);
          return [];
        });
    };

    // CSS only shows the .active tab; if the default one is hidden, promote a
    // rendered tab so the section isn't left visually empty.
    const ensureActiveTab = (productBottom, preferredTabName = null) => {
      if (!productBottom) return;
      const contents = Array.from(
        productBottom.querySelectorAll(".tabs-content > [data-tab]"),
      );
      const rendered = contents.filter(
        (c) =>
          c.dataset.psykheRendered === "true" &&
          !c.classList.contains("hide"),
      );
      if (!rendered.length) return;
      const preferred = preferredTabName
        ? rendered.find((c) => c.getAttribute("data-tab") === preferredTabName)
        : null;
      if (preferred) {
        const tabName = preferred.getAttribute("data-tab");
        contents.forEach((c) => c.classList.toggle("active", c === preferred));
        productBottom
          .querySelectorAll(".tab-titles > [data-tab]")
          .forEach((btn) =>
            btn.classList.toggle(
              "active",
              btn.getAttribute("data-tab") === tabName,
            ),
          );
        return;
      }
      if (rendered.some((c) => c.classList.contains("active"))) return;
      const target = rendered[0];
      const tabName = target.getAttribute("data-tab");
      contents.forEach((c) => c.classList.toggle("active", c === target));
      productBottom
        .querySelectorAll(".tab-titles > [data-tab]")
        .forEach((btn) =>
          btn.classList.toggle(
            "active",
            btn.getAttribute("data-tab") === tabName,
          ),
        );
    };

    // fetch brand first so its scarce designer items win cross-carousel
    // dedupe, while similar stays the default visible tab.
    const containerList = Array.from(containers);
    const brandContainer = containerList.find(
      (c) => c.getAttribute("data-psykhe-reco") === "brand",
    );
    const similarContainer = containerList.find(
      (c) => c.getAttribute("data-psykhe-reco") === "similar",
    );
    const sequenceDedupe = Boolean(brandContainer && similarContainer);

    containerList.forEach((container) => {
      if (
        sequenceDedupe &&
        (container === brandContainer || container === similarContainer)
      ) {
        return;
      }
      const productBottom = container.closest(".product-bottom");
      processCarousel(container).then(() => ensureActiveTab(productBottom));
    });

    if (sequenceDedupe) {
      const productBottom = similarContainer.closest(".product-bottom");
      processCarousel(brandContainer, [], { revealAfterRender: false })
        .then((displayedBtdIds) => {
          return processCarousel(similarContainer, displayedBtdIds);
        })
        .then(() => {
          if (isCarouselTabRendered(brandContainer)) {
            setCarouselTabRendered(brandContainer, true);
          }
          ensureActiveTab(productBottom, "recommended");
        });
    }
  };

  // ============================================
  // Shopify Fallback Search
  // ============================================
  let consecutiveApiFailures = 0;
  let isUsingFallback = false;
  let fallbackResetTimer = null;

  const resetFallbackState = () => {
    consecutiveApiFailures = 0;
    isUsingFallback = false;
    if (fallbackResetTimer) {
      clearTimeout(fallbackResetTimer);
      fallbackResetTimer = null;
    }
    debugLog("Fallback state reset");
  };

  const scheduleFallbackReset = () => {
    if (fallbackResetTimer) return;
    fallbackResetTimer = setTimeout(() => {
      fallbackResetTimer = null;
      resetFallbackState();
      debugLog("Fallback cooldown elapsed, attempting Psykhe API again");
    }, FALLBACK_COOLDOWN_MS);
  };

  const incrementApiFailure = () => {
    consecutiveApiFailures++;
    debugLog(
      `API failure count: ${consecutiveApiFailures}/${MAX_API_FAILURES}`,
    );
    if (consecutiveApiFailures >= MAX_API_FAILURES) {
      isUsingFallback = true;
      scheduleFallbackReset();
      debugLog("Switching to Shopify fallback search");
    }
  };

  window.psykhe.fallback = {
    get isActive() {
      return isUsingFallback;
    },
    get failureCount() {
      return consecutiveApiFailures;
    },
    get maxFailures() {
      return MAX_API_FAILURES;
    },
    reset: () => {
      resetFallbackState();
      debugLog("Fallback manually reset via window.psykhe.fallback.reset()");
    },
    forceEnable: () => {
      isUsingFallback = true;
      scheduleFallbackReset();
      debugLog(
        "Fallback manually enabled via window.psykhe.fallback.forceEnable()",
      );
    },
  };

  const shopifySearch = async (query, options = {}) => {
    const params = new URLSearchParams({
      q: query,
      "resources[type]": "product",
      "resources[limit]": String(options.limit || MAX_PRODUCTS_TO_SHOW),
      "resources[options][unavailable_products]": "hide",
      "resources[options][fields]": FALLBACK_SEARCH_FIELDS,
    });

    const timeoutSignal = createTimeoutSignal(API_TIMEOUT);
    const signal = mergeSignals(options.signal, timeoutSignal);

    const response = await fetch(`${SHOPIFY_SEARCH_URL}?${params}`, { signal });
    if (!response.ok) throw new Error("HTTP error " + response.status);
    return response.json();
  };

  const transformShopifyResponse = (shopifyData) => {
    const products = shopifyData?.resources?.results?.products || [];

    const transformedProducts = products.map((product) => ({
      type: "product",
      handle: product.handle,
      name: product.title,
      vendor: product.vendor || "",
      images: product.image ? [product.image] : [],
      main_image: product.image || null,
      variants: [
        {
          identifier: product.id ? String(product.id) : null,
          attributes: {
            price: product.price ? parseFloat(product.price) : null,
            compareAtPrice: product.compare_at_price
              ? parseFloat(product.compare_at_price)
              : null,
          },
        },
      ],
    }));

    return {
      items: transformedProducts,
      facets: [],
      total: products.length,
      redirect: null,
      reco_id: null,
      _fallback: true,
    };
  };

  // ============================================
  // Tracking (non-blocking)
  // ============================================
  let searchTrackingTimer = null;
  let pendingSearchEvent = null;

  const trackEvent = (eventName, event) => {
    try {
      if (!window.Shopify?.analytics) {
        debugLog(`Shopify analytics not available, skipping ${eventName}`);
        return;
      }
      debugLog(`Tracking ${eventName}`, event);
      window.Shopify.analytics.publish(eventName, event);
    } catch {
      /* no-op */
    }
  };

  const trackSearch = (event) => trackEvent(TRACKING_EVENT_SEARCH, event);
  const trackPromoClick = (event) =>
    trackEvent(TRACKING_EVENT_PROMO_CLICK, event);
  const trackCarouselView = (event) =>
    trackEvent(TRACKING_EVENT_LIST_VIEW, event);
  const trackCarouselClick = (event) =>
    trackEvent(TRACKING_EVENT_LIST_CLICK, event);

  const isTrackingCurrent = (requestId, query) =>
    isSearchActive &&
    requestId === currentRequestId &&
    query === (searchInput?.value?.trim() || "");

  const trackSearchDebounced = (event, requestId) => {
    clearTimeout(searchTrackingTimer);
    if (!isTrackingCurrent(requestId, event.searchQuery)) {
      pendingSearchEvent = null;
      return;
    }
    if (event.resultsCount === 0) {
      trackSearch(event);
      pendingSearchEvent = null;
      return;
    }
    pendingSearchEvent = { event, requestId, query: event.searchQuery };
    searchTrackingTimer = setTimeout(() => {
      if (!pendingSearchEvent) return;
      const {
        event: pendingEvent,
        requestId: pendingId,
        query,
      } = pendingSearchEvent;
      pendingSearchEvent = null;
      if (!isTrackingCurrent(pendingId, query)) return;
      trackSearch(pendingEvent);
    }, SEARCH_TRACKING_DELAY);
  };

  const cancelPendingTracking = () => {
    clearTimeout(searchTrackingTimer);
    searchTrackingTimer = null;
    pendingSearchEvent = null;
  };

  const flushPendingTracking = () => {
    clearTimeout(searchTrackingTimer);
    if (pendingSearchEvent) {
      const { event, requestId, query } = pendingSearchEvent;
      debugLog("Flushing pending search tracking");
      if (isTrackingCurrent(requestId, query)) {
        trackSearch(event);
      }
      pendingSearchEvent = null;
    }
  };

  const buildTrackingProducts = (products) => {
    if (!Array.isArray(products)) return [];
    const currency = getCurrency();
    return products
      .filter((p) => p.type === "product" && p.handle)
      .map((product, index) => {
        const variant = product.variants?.[0];
        return {
          identifier: product.handle,
          name: product.name || "",
          price: variant?.attributes?.price || 0,
          position: index,
          currency,
          ...(variant?.identifier && { variantId: String(variant.identifier) }),
        };
      });
  };

  // ============================================
  // DOM Elements
  // ============================================
  const $ = (selector, parent = document) => parent?.querySelector(selector);
  const searchWrapper = $(".psykhe-search-wrapper");
  const searchTrigger = document.getElementById("psykhe-search-trigger");
  const mobileSearchTrigger = $(
    ".header-block.block-search.block-search--mobile",
  );
  const formContainer = $(".psykhe-search-form-container", searchWrapper);
  const searchForm = $(".psykhe-search-form", searchWrapper);
  const searchInput = document.getElementById("psykhe-search-input");
  const resultsContainer = document.getElementById(
    "psykhe-search-results-container",
  );
  const resultsContent = $(".psykhe-search-results-content", resultsContainer);
  const categoryList = $(
    ".psykhe-search-column-category .psykhe-search-facets-list",
    resultsContainer,
  );
  const designerList = $(
    ".psykhe-search-column-designer .psykhe-search-facets-list",
    resultsContainer,
  );
  const productGrid = $(".psykhe-search-products-grid", resultsContainer);
  const seeAllLink = $(".psykhe-search-see-all", resultsContainer);
  const totalCountSpan = $(".psykhe-search-total-count", resultsContainer);
  const queryTermSpan = $(".psykhe-search-query-term", resultsContainer);
  const loadingIndicator = $(".psykhe-search-loading", resultsContainer);
  const noResultsMessage = $(".psykhe-search-no-results", resultsContainer);
  const activeCloseButton = $(".psykhe-active-close-btn", searchWrapper);
  const inputWrapper = $(".psykhe-input-wrapper", searchWrapper);

  // ============================================
  // State Management
  // ============================================
  let currentQuery = "";
  let pendingQuery = "";
  let currentAbortController = null;
  let currentRequestId = 0;
  let isSearchActive = false;
  let searchState = {
    redirectPath: null,
    redirectSource: null,
    recommendationId: null,
    redirectQuery: null,
  };

  const resetSearchState = () => {
    searchState = {
      redirectPath: null,
      redirectSource: null,
      recommendationId: null,
      redirectQuery: null,
    };
  };

  const abortCurrentSearch = () => {
    if (currentAbortController?.abort) {
      currentAbortController.abort();
      currentAbortController = null;
    }
  };

  // ============================================
  // Keyboard Navigation
  // ============================================
  let focusedResultIndex = -1;

  const getFocusableResults = () => {
    if (!resultsContainer) return [];
    return Array.from(
      resultsContainer.querySelectorAll(
        ".psykhe-search-product-item a, .psykhe-search-facets-list a",
      ),
    );
  };

  const setFocusedResult = (index) => {
    const items = getFocusableResults();
    if (!items.length) return;

    items.forEach((item) => item.classList.remove("is-keyboard-focused"));

    if (index < -1) index = items.length - 1;
    if (index >= items.length) index = 0;

    focusedResultIndex = index;

    if (index >= 0 && items[index]) {
      if (!items[index].id) {
        items[index].id = `psykhe-result-${index}`;
      }
      items[index].classList.add("is-keyboard-focused");
      items[index].focus();
      searchInput?.setAttribute("aria-activedescendant", items[index].id);
      announceToScreenReader(items[index].textContent || "Result item");
    } else {
      searchInput?.removeAttribute("aria-activedescendant");
    }
  };

  const resetKeyboardNavigation = () => {
    focusedResultIndex = -1;
    const items = getFocusableResults();
    items.forEach((item) => item.classList.remove("is-keyboard-focused"));
    searchInput?.removeAttribute("aria-activedescendant");
  };

  // ============================================
  // UI Functions
  // ============================================
  const clearResults = () => {
    resetKeyboardNavigation();
    if (categoryList) categoryList.innerHTML = "";
    if (designerList) designerList.innerHTML = "";
    if (productGrid) productGrid.innerHTML = "";
    if (seeAllLink) seeAllLink.style.display = "none";
    if (totalCountSpan) totalCountSpan.textContent = "0";
    if (queryTermSpan) queryTermSpan.textContent = "";
    if (loadingIndicator) loadingIndicator.style.display = "none";
    if (noResultsMessage) noResultsMessage.style.display = "none";
  };

  const showResults = () => {
    if (searchWrapper?.classList.contains("is-active") && resultsContainer) {
      resultsContainer.style.display = "block";
      searchInput?.setAttribute("aria-expanded", "true");
    }
  };

  const hideResults = () => {
    if (resultsContainer) resultsContainer.style.display = "none";
    searchInput?.setAttribute("aria-expanded", "false");
  };

  let activateFocusTimer = null;
  const activateSearch = () => {
    if (!searchWrapper) return;
    isSearchActive = true;
    if (!searchWrapper.classList.contains("is-inactive")) return;
    searchWrapper.classList.remove("is-inactive");
    searchWrapper.classList.add("is-active");
    clearTimeout(activateFocusTimer);
    activateFocusTimer = setTimeout(() => searchInput?.focus(), 50);
  };

  const deactivateSearch = () => {
    if (!searchWrapper) return;
    isSearchActive = false;
    clearTimeout(activateFocusTimer);
    activateFocusTimer = null;
    debouncedSearch.cancel();
    abortCurrentSearch();
    cancelPendingTracking();
    cleanupSrAnnouncer();
    searchWrapper.classList.remove("is-active");
    searchWrapper.classList.add("is-inactive");
    hideResults();
    clearResults();
    if (searchInput) searchInput.value = "";
    currentQuery = "";
    pendingQuery = "";
    resetSearchState();
  };

  const showNoResults = (query) => {
    if (resultsContent) resultsContent.style.display = "none";
    if (noResultsMessage) {
      const text = query
        ? `No results found for "${query}".`
        : USER_ERROR_MESSAGE;
      noResultsMessage.textContent = text;
      noResultsMessage.style.display = "block";
      announceToScreenReader(text);
    }
    if (seeAllLink) seeAllLink.style.display = "none";
  };

  // ============================================
  // Category Formatting
  // ============================================
  const formatCategoryLabel = (label) => {
    if (!label) return "";
    const segments = label.split("->").map((s) => s.trim());
    if (segments.length === 1) return toTitleCase(segments[0]);
    if (segments.length === 2) {
      return `${toTitleCase(segments[0])} / ${toTitleCase(segments[1])}`;
    }
    return `${toTitleCase(segments[1])} / ${toTitleCase(segments[2])}`;
  };

  const getCategoryDepth = (label) =>
    label ? label.split("->").length - 1 : 0;

  // ============================================
  // Rendering Functions
  // ============================================
  const renderFacets = (
    facetData,
    listElement,
    maxItems,
    query,
    sortByCount = false,
  ) => {
    if (!listElement) return;
    listElement.innerHTML = "";

    if (!facetData?.values?.length) {
      const noOptions = document.createElement("li");
      noOptions.textContent = "No options found";
      noOptions.style.cssText = "font-style: italic; opacity: 0.7;";
      listElement.appendChild(noOptions);
      return;
    }

    const isCategory = facetData.key === FACET_KEY_CATEGORY;
    const facets = facetData.values
      .map((option) => {
        const rawLabel = option.label || option.key;
        return {
          option,
          rawLabel,
          depth: isCategory ? getCategoryDepth(rawLabel) : 0,
          displayLabel: isCategory ? formatCategoryLabel(rawLabel) : rawLabel,
        };
      })
      .sort((a, b) => {
        if (isCategory) {
          if (a.depth === 0 && b.depth > 0) return 1;
          if (a.depth > 0 && b.depth === 0) return -1;
        }
        return sortByCount || isCategory
          ? (b.option.count || 0) - (a.option.count || 0)
          : a.rawLabel.localeCompare(b.rawLabel);
      });

    const seen = new Set();
    const unique = facets.filter(({ displayLabel }) => {
      if (seen.has(displayLabel)) return false;
      seen.add(displayLabel);
      return true;
    });

    const ul = document.createElement("ul");
    unique.slice(0, maxItems).forEach(({ option, displayLabel, rawLabel }) => {
      const li = document.createElement("li");
      const link = document.createElement("a");
      const facetValue = option.label || rawLabel || "";
      link.href = facetValue
        ? buildSearchUrl(query, facetData.key, facetValue)
        : buildSearchUrl(query);
      link.textContent = displayLabel;
      link.setAttribute("aria-label", `Filter by ${displayLabel}`);
      li.appendChild(link);
      ul.appendChild(li);
    });
    listElement.appendChild(ul);
  };

  const renderProducts = (products, maxItems) => {
    if (!productGrid) return;
    productGrid.innerHTML = "";
    productGrid.setAttribute("role", "list");
    productGrid.setAttribute("aria-label", "Search results");
    if (!products?.length) return;

    const fragment = document.createDocumentFragment();
    products.slice(0, maxItems).forEach((product) => {
      if (product.type !== "product" || !product.handle) return;

      const item = document.createElement("div");
      item.className = "psykhe-search-product-item";
      item.setAttribute("role", "listitem");

      const link = document.createElement("a");
      link.href = `/products/${encodeURIComponent(product.handle)}`;

      const imgWrapper = document.createElement("div");
      imgWrapper.className = "psykhe-search-product-image-wrapper";

      const img = document.createElement("img");
      const imageUrl = product.main_image || product.images?.[0];
      if (imageUrl) {
        img.src = imageUrl;
        img.alt = product.name || "Product image";
        img.loading = "lazy";
        img.width = 185;
        img.height = 246;
      }
      imgWrapper.appendChild(img);

      const details = document.createElement("div");
      details.className = "psykhe-search-product-details";

      if (product.vendor) {
        const brand = document.createElement("div");
        brand.className = "psykhe-search-product-brand";
        brand.textContent = product.vendor;
        details.appendChild(brand);
      }

      const title = document.createElement("div");
      title.className = "psykhe-search-product-title";
      title.textContent = product.name || "";
      details.appendChild(title);

      const variant = product.variants?.[0];
      const currentPrice = variant?.attributes?.price;
      const compareAtPrice = variant?.attributes?.compareAtPrice;

      if (currentPrice != null) {
        const priceContainer = document.createElement("div");
        priceContainer.className = "psykhe-product-price-container";

        const onSale = compareAtPrice != null && compareAtPrice > currentPrice;
        if (onSale) {
          const compareEl = document.createElement("span");
          compareEl.className = "psykhe-product-price-compare";
          compareEl.textContent = formatPrice(compareAtPrice);
          priceContainer.appendChild(compareEl);
        }

        const priceEl = document.createElement("span");
        priceEl.className = "psykhe-product-price-current";
        if (onSale) priceEl.classList.add("is-on-sale");
        priceEl.textContent = formatPrice(currentPrice);
        priceContainer.appendChild(priceEl);
        details.appendChild(priceContainer);
      }

      const ariaLabelParts = [];
      if (product.vendor) ariaLabelParts.push(product.vendor);
      if (product.name) ariaLabelParts.push(product.name);
      if (currentPrice != null) ariaLabelParts.push(formatPrice(currentPrice));
      link.setAttribute("aria-label", ariaLabelParts.join(", ") || "Product");

      link.appendChild(imgWrapper);
      link.appendChild(details);
      item.appendChild(link);
      fragment.appendChild(item);
    });

    productGrid.appendChild(fragment);
  };

  // ============================================
  // Search Logic
  // ============================================
  const performSearch = async (query) => {
    if (
      !loadingIndicator ||
      !noResultsMessage ||
      !seeAllLink ||
      !totalCountSpan ||
      !queryTermSpan
    ) {
      return;
    }

    if (query.length < MIN_QUERY_LENGTH) {
      clearResults();
      currentQuery = query;
      pendingQuery = query;
      return;
    }

    if (!isSearchActive) return;

    const thisRequestId = ++currentRequestId;
    const thisAbortController =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    abortCurrentSearch();
    currentAbortController = thisAbortController;
    pendingQuery = query;
    loadingIndicator.style.display = "block";
    noResultsMessage.style.display = "none";
    announceToScreenReader("Searching...", "polite");
    showResults();

    const isStale = () => thisRequestId !== currentRequestId || !isSearchActive;

    let data = null;
    let usedFallback = isUsingFallback;

    if (!isUsingFallback && typeof window.psykhe?.search === "function") {
      try {
        const response = await window.psykhe.search(query, {
          limit: MAX_PRODUCTS_TO_SHOW,
          signal: thisAbortController?.signal,
        });

        if (isStale()) return;

        if (response.errors) {
          throw new Error(
            "API returned errors: " + JSON.stringify(response.errors),
          );
        }

        data = response.data || response;
        if (consecutiveApiFailures > 0) {
          resetFallbackState();
        }
        debugLog("Psykhe API success", { total: data.total || 0 });
      } catch (error) {
        if (error.name === "AbortError") return;
        debugWarn("Psykhe API error:", error);
        incrementApiFailure();
        if (isStale()) return;
        usedFallback = true;
      }
    } else if (!isUsingFallback) {
      debugWarn("Psykhe API not available, using fallback");
      usedFallback = true;
    }

    if (!data && usedFallback) {
      try {
        debugLog("Using Shopify fallback search");
        const shopifyResponse = await shopifySearch(query, {
          limit: MAX_PRODUCTS_TO_SHOW,
          signal: thisAbortController?.signal,
        });

        if (isStale()) return;

        data = transformShopifyResponse(shopifyResponse);
        debugLog("Shopify fallback success", { total: data.total || 0 });
      } catch (fallbackError) {
        if (fallbackError.name === "AbortError") return;
        if (isStale()) return;
        debugWarn("Shopify fallback error:", fallbackError);
        loadingIndicator.style.display = "none";
        if (currentAbortController === thisAbortController) {
          currentAbortController = null;
        }
        clearResults();
        currentQuery = query;
        showNoResults(null);
        return;
      }
    }

    if (isStale()) return;

    loadingIndicator.style.display = "none";
    if (currentAbortController === thisAbortController) {
      currentAbortController = null;
    }

    if (!data) {
      clearResults();
      currentQuery = query;
      showNoResults(query);
      return;
    }

    const products = data.items || [];
    const facets = data.facets || [];
    const total = data.total || 0;
    const redirect = data.redirect;

    currentQuery = query;
    searchState = {
      redirectPath: redirect?.path || null,
      redirectSource: redirect?.source || null,
      recommendationId: data.reco_id || null,
      redirectQuery: query,
    };

    trackSearchDebounced(
      {
        searchQuery: query,
        resultsCount: total,
        products: buildTrackingProducts(products),
        recommendationId: searchState.recommendationId,
        searchSource: usedFallback ? "shopify_fallback" : "psykhe_ai",
        redirectUrl: searchState.redirectPath,
        redirectSource: searchState.redirectSource,
      },
      thisRequestId,
    );

    const findFacet = (labelMatch, keyMatch) =>
      facets.find(
        (f) => f.label?.toUpperCase() === labelMatch || f.key === keyMatch,
      );

    const categoryFacet = findFacet("CATEGORY", FACET_KEY_CATEGORY);
    const designerFacet = findFacet("DESIGNER", FACET_KEY_VENDOR);

    const hasContent =
      products.length > 0 ||
      categoryFacet?.values?.length > 0 ||
      designerFacet?.values?.length > 0;

    if (!hasContent) {
      clearResults();
      currentQuery = query;
      showNoResults(query);
      return;
    }

    if (resultsContent) resultsContent.style.display = "flex";
    renderFacets(categoryFacet, categoryList, MAX_FACETS_TO_SHOW, query, true);
    renderFacets(designerFacet, designerList, MAX_FACETS_TO_SHOW, query, true);
    renderProducts(products, MAX_PRODUCTS_TO_SHOW);

    const shownCount = Math.min(products.length, MAX_PRODUCTS_TO_SHOW);
    const fallbackNote = usedFallback ? " (using backup search)" : "";
    announceToScreenReader(
      total === 1
        ? `1 result found${fallbackNote}. Use arrow keys to navigate.`
        : `${total} results found, showing ${shownCount}${fallbackNote}. Use arrow keys to navigate.`,
    );

    if (total > 0) {
      totalCountSpan.textContent = total;
      queryTermSpan.textContent = query;
      seeAllLink.href = searchState.redirectPath || buildSearchUrl(query);
      seeAllLink.style.display = "inline";
      bindSeeAllTracking();
    } else {
      seeAllLink.style.display = "none";
    }

    noResultsMessage.style.display = "none";
  };

  const bindSeeAllTracking = () => {
    if (seeAllLink.dataset.trackingBound) return;
    seeAllLink.dataset.trackingBound = "true";
    seeAllLink.addEventListener("click", () => {
      flushPendingTracking();
      const inputQuery = searchInput?.value?.trim() || "";
      if (
        searchState.redirectPath &&
        searchState.recommendationId &&
        searchState.redirectQuery === inputQuery
      ) {
        trackPromoClick({
          recommendationId: searchState.recommendationId,
          promo: {
            sourceId: searchState.redirectSource,
            type: "redirect",
            position: null,
            targetUrl: searchState.redirectPath,
          },
        });
      }
    });
  };

  const debouncedSearch = debounce(performSearch, DEBOUNCE_DELAY);

  // ============================================
  // Event Handlers
  // ============================================
  const handleInputClear = () => {
    debouncedSearch.cancel();
    abortCurrentSearch();
    cancelPendingTracking();
    cancelSrAnnouncement();
    hideResults();
    clearResults();
    currentQuery = "";
    pendingQuery = "";
    resetSearchState();
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const query = searchInput?.value?.trim() || "";
    if (query.length < MIN_QUERY_LENGTH) return;

    flushPendingTracking();
    const useRedirect =
      searchState.redirectPath && searchState.redirectQuery === query;

    if (useRedirect) {
      if (searchState.recommendationId) {
        trackPromoClick({
          recommendationId: searchState.recommendationId,
          promo: {
            sourceId: searchState.redirectSource,
            type: "redirect",
            position: null,
            targetUrl: searchState.redirectPath,
          },
        });
      }
      window.location.href = searchState.redirectPath;
    } else {
      window.location.href = buildSearchUrl(query);
    }
  };

  const handleInputChange = () => {
    const query = searchInput?.value?.trim() || "";
    if (query.length === 0) {
      handleInputClear();
    } else if (query.length >= MIN_QUERY_LENGTH) {
      if (query !== pendingQuery) {
        debouncedSearch(query);
      } else {
        debouncedSearch.cancel();
      }
    } else {
      debouncedSearch.cancel();
      abortCurrentSearch();
      clearResults();
      currentQuery = query;
      pendingQuery = query;
      hideResults();
    }
  };

  const handleInputFocus = () => {
    activateSearch();
    const query = searchInput?.value?.trim() || "";
    if (query.length < MIN_QUERY_LENGTH) return;

    debouncedSearch.cancel();

    if (query !== pendingQuery) {
      performSearch(query);
    } else {
      const hasContent =
        productGrid?.hasChildNodes() ||
        categoryList?.hasChildNodes() ||
        designerList?.hasChildNodes();
      if (hasContent) {
        showResults();
      } else if (!currentAbortController) {
        performSearch(query);
      } else {
        showResults();
      }
    }
  };

  const handleClickOutside = (event) => {
    if (!searchWrapper?.classList.contains("is-active")) return;
    if (searchWrapper.contains(event.target)) return;
    if (mobileSearchTrigger?.contains(event.target)) return;
    deactivateSearch();
  };

  const handleKeyboardNavigation = (event) => {
    if (!searchWrapper?.classList.contains("is-active")) return;

    const resultsVisible = resultsContainer?.style.display === "block";

    switch (event.key) {
      case "Escape":
        if (resultsVisible) {
          hideResults();
          resetKeyboardNavigation();
          searchInput?.focus();
        } else {
          deactivateSearch();
        }
        break;

      case "ArrowDown":
        if (resultsVisible) {
          event.preventDefault();
          setFocusedResult(focusedResultIndex + 1);
        }
        break;

      case "ArrowUp":
        if (resultsVisible) {
          event.preventDefault();
          if (focusedResultIndex <= 0) {
            resetKeyboardNavigation();
            searchInput?.focus();
          } else {
            setFocusedResult(focusedResultIndex - 1);
          }
        }
        break;

      case "Tab":
        if (resultsVisible) {
          resetKeyboardNavigation();
        }
        break;
    }
  };

  // ============================================
  // Recommendations
  // ============================================
  window.psykhe.initRecommendations();

  // ============================================
  // Search UI Initialization
  // ============================================
  const requiredElements = [
    searchWrapper,
    formContainer,
    searchInput,
    resultsContainer,
    activeCloseButton,
    inputWrapper,
  ];
  const hasTrigger = searchTrigger || mobileSearchTrigger;

  if (!requiredElements.every(Boolean) || !hasTrigger) {
    debugWarn(
      "Psykhe Search Initialization Failed: Required elements missing.",
    );
    return;
  }

  searchTrigger?.addEventListener("click", () => activateSearch());
  searchTrigger?.addEventListener("focus", () => activateSearch());
  mobileSearchTrigger?.addEventListener("click", () => activateSearch());
  mobileSearchTrigger?.addEventListener("focus", () => activateSearch());
  searchForm?.addEventListener("submit", handleFormSubmit);
  searchInput.addEventListener("input", handleInputChange);
  searchInput.addEventListener("focus", handleInputFocus);
  activeCloseButton.addEventListener("click", (e) => {
    e.stopPropagation();
    deactivateSearch();
  });
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyboardNavigation);

  window.psykhe._initialized = true;
  debugLog("Psykhe Search initialized successfully");
});